import {Button, Card, Form, Input, Select, Typography, Collapse, Row, Col, Switch, TimePicker, Divider} from "antd";
import ReactJson from '@microlink/react-json-view';
import React, {useEffect, useState} from "react";
import { Edit, useDataProvider, useEditContext, useNotify, useQueryWithStore, useRedirect, usePermissions } from 'react-admin';
import {ExtendedDataProvider, SelectItem} from "@src/types";
import HTTPRequestForm from "../Common/HTTPRequestForm";
import StateSelector from "@src/components/ChangeInstances/StatesSelector";
import DeleteButton from "@src/components/Common/DeleteButton";
import dayjs from "dayjs";

const { Title, Paragraph, Text }= Typography;
const { Option } = Select;
const { TextArea } = Input;


// @ts-ignore
const getDurationSeconds = (timeValue) => {
    if (!timeValue) return null;
    const date = timeValue.toDate(); // Convert moment to JS Date
    return date.getMinutes() * 60 + date.getSeconds();
};

const secondsToMoment = (sec: number) => {
    const mins = Math.floor(sec / 60);
    const secs = sec % 60;
    return dayjs().startOf('day').minute(mins).second(secs);
};

const CreateWebhookForm = (props: any) => {
    const { permissions } = usePermissions();
    const [form] = Form.useForm();
    const notify = useNotify();
    const redirect = useRedirect();
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const { record, loading, loaded} = useEditContext();
    const [services, setServices] = useState<SelectItem[] | null>(null);
    const { loaded: servicesLoaded, error: servicesError, data: serviceData } = useQueryWithStore({
        type: 'getList',
        resource: 'services',
        payload: {
            pagination: { page: 1, perPage: 20 },
            sort: { field: 'name', order: 'asc' },
            filter: {}
        }
    });


    useEffect(() => {
        const services: SelectItem[] = []
        if (serviceData) {
            for (const item of serviceData) {
                services.push({ text: item.name, value: item.id });
            }
            setServices(services);
        }
    }, [serviceData]);

    useEffect(() => {
        if (record) {
            const headers_: {}[] = []
            if (record.headers) {
                Object.entries(record.headers).map(([key, value]) => headers_.push(
                  { header_key: key, header_value: value })
                )
            }
            form.setFieldsValue({headers: headers_});
        }
    }, []);


    const onSubmit = (values: any) => {
        if (values.headers){
            values['headers'] = Object.fromEntries(values.headers.map(
              (x: { header_key: string; header_value: string; }
              ) => [x.header_key, x.header_value]))
        }

        values = {
            ...values,
            trigger_wait_time: getDurationSeconds(values.trigger_wait_time),
            trigger_cooling_time: getDurationSeconds(values.trigger_cooling_time),
        };

        if (record){
            if (typeof values.service == "object") {
                values.service = values.service.value
            }
            dataProvider.update('webhooks', { id: record.id, data: values, previousData: { id: record.id } })
              .then(() => {
                  notify('Webhook updated successfully');
                  redirect('/webhooks')
              })
              .catch((error: any) => {
                  if (error.status == 400) {
                      for (const [key, value] of Object.entries<any>(error.body)) {
                          form.setFields([
                              { name: key, errors: value }
                          ])
                      }
                  }
              })
        } else {
            dataProvider.create('webhooks', {data: values})
              .then(() => {
                  notify('Webhook created successfully');
                  redirect('/webhooks')
              })
              .catch((error: any) => {
                  if (error.status == 400)
                      for (const [key, value] of Object.entries<any>(error.body)){
                          if(key==="non_field_errors")
                              notify(value[0], "error")
                          else
                              form.setFields([{name: key, errors: value}])
                      }
              })
        }
    }

    return (
        <Form
            form={form}
            name="create_webhook"
            labelCol={{ span: 4 }}
            style={{margin: "10px"}}
            className="row-col"
            labelAlign="left"
            initialValues={
                record?
                    {
                        ...record,
                        service: { value: record.service.id, label: record.service.name },
                        trigger_wait_time: record.trigger_wait_time != null ? secondsToMoment(record.trigger_wait_time) : null,
                        trigger_cooling_time: record.trigger_cooling_time != null ? secondsToMoment(record.trigger_cooling_time) : null,
                    }
                    :
                    { remember: true, trust_env: true, verify: true }
            }
            onFinish={onSubmit}
        >
            <Form.Item
                label="Webhook Name"
                name="name"
                rules={[{ required: true, message: 'Please enter Webhook Name' }]}
            >
                <Input placeholder="Name" disabled={permissions !== "ADMIN"}/>
            </Form.Item>
            <Form.Item label="Description" name="description">
                <TextArea
                  showCount
                  maxLength={200}
                  style={{ height: 120, resize: 'none' }}
                  placeholder="Description"
                  disabled={permissions !== "ADMIN"}
                />
            </Form.Item>

            <Form.Item
                label="Service"
                name="service"
                rules={[{ required: true, message: 'Please select a service' }]}
                // initialValue={record && record.service.id}
            >
                <Select
                    placeholder="Select Service"
                    style={{ width: '100%' }}
                    disabled={permissions !== "ADMIN"}
                >
                    {services && services.map(service => <Option key={service.value} value={service.value}>{service.text}</Option>)}
                </Select>
            </Form.Item>

            <Divider titlePlacement="start" plain><Text strong>HTTP Request Configuration</Text></Divider>
            <HTTPRequestForm form={form} />

            <Divider titlePlacement="start" plain><Text strong>Trigger Options</Text></Divider>
            <Form.Item name={"change_instance_state"} label={"Change Instance State"} initialValue={null}>
                <StateSelector   />
            </Form.Item>

            <Row>
                <Col span={12}>
                    <Form.Item name="trigger_wait_time" label="Trigger Wait Time" labelCol={{ span: 8 }} rules={[{required: true}]}>
                        <TimePicker format="mm:ss" showNow={false} style={{ width: "200px" }}/>
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item name="trigger_cooling_time" label="Cooling Time" labelCol={{ span: 8 }} rules={[{required: true}]}>
                        <TimePicker format="mm:ss" showNow={false} style={{ width: "200px" }}/>
                    </Form.Item>
                </Col>
            </Row>

            <Form.Item>
                {
                    permissions === "ADMIN" &&
                    <Button style={{ width: "20%" }} type="primary" htmlType="submit" className="form-button">{record? "Update":"Create"}</Button>
                }
                {
                    record &&
                    permissions === "ADMIN" &&
                    <DeleteButton title={"Webhook"} resource={"webhooks"} style={{ width: "20%", float: "right" }} record={record} />
                }
            </Form.Item>
        </Form>
    )
}

const WebhookCreate = (props: any) => {
    return (
      <>
          <Card
            className=" header-solid h-full ant-card pt-0"
            bordered={false}
          >
            <Title level={4}>Webhook</Title>
            <Collapse defaultActiveKey={[]} items={[{
              key: '1',
              label: 'How it works?',
              children: (
                <Typography>
                  <Paragraph>
                    Webhooks are triggered automatically whenever a consumer's submission generates
                    any change instances for the service selected in the webhook. It is also possible to
                    trigger webhooks manually from the Webhooks List.
                  </Paragraph>
                  <Paragraph>
                    <ul>
                      <li>
                        The webhook is sent to the target url defined below
                      </li>
                      <li>
                        Authentication can be added using the "Authorization" header field.
                        Some examples for this field are as follows:<br />
                        <Text code>Api-Key "api key"</Text>
                        <Text code>Token "token"</Text>
                        <Text code>Bearer/JWT "token"</Text>
                      </li>
                      <li>
                        The body of the webhook request is a json that contains the
                        service's id assigned in NetOrca:
                        <ReactJson
                          style={{ padding: '20px' }}
                          src={{service: 1}}
                          collapsed={false}
                          enableClipboard={false}
                          displayArrayKey={false}
                          displayDataTypes={false}
                          name="body"
                          displayObjectSize={false}
                        />
                      </li>
                      <li>
                        The responses are stored in "responses" tab in Webhooks' list page. Each response
                        contains the status code and the body returned from target endpoint
                      </li>
                      <li>
                        By default, only the last 5 responses are stored in our database
                      </li>
                      <li>
                        Manual triggers are limited to 3 times per minute.
                      </li>
                    </ul>
                  </Paragraph>
                </Typography>
              ),
            }]} />
              {props.id &&
                <Edit {...props}>
                    <CreateWebhookForm />
                </Edit>
              }
              {!props.id && <CreateWebhookForm />}
          </Card>
      </>
    )
}

export default WebhookCreate;